// CREDITS (BEETJE) NAAR https://www.dynamsoft.com/codepool/web-document-scanner-with-opencvjs.html

import React, { useEffect, useState } from 'react';
import BarcodeScanner from './(components)/DocumentScanner';
import { CameraIcon, ChevronLeft, CirclePlusIcon, DownloadIcon, EditIcon, FileDownIcon, PlusIcon, ScanLineIcon, Trash2Icon, TrashIcon } from 'lucide-react';
import NoImages from 'src/assets/images/no-images.svg'
import { format } from 'date-fns';
import { v4 as uuid } from 'uuid'
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { Sheet, SheetClose, SheetContent, SheetTrigger } from 'src/components/ui/sheet';
import localforage from 'localforage'
import BundleSheet from './(components)/BundleSheet';
import toast from 'react-hot-toast';

declare const cv: any;

export type Bundle = {
  id: string;
  name: string;
  date: string; //iso string
  documents: string[]; //base64
}

const DocumentScanner: React.FC = () => {
    const [bundles, setbundles] = useState<Bundle[]>([]);

    useEffect(()=>{
      async function loadBundles() {
        const b = await localforage.getItem('sb');
        if(b){ setbundles(b as Bundle[]); }
      }
      loadBundles()
    }, [])

    useEffect(()=>{
      localforage.setItem('sb', bundles)
    }, [bundles])

    const base64ToUint8Array = (base64:string) => {
      const base64String = base64.split(',')[1];
      const binaryString = atob(base64String);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    };

    const generatePDF = async (base64Array:string[], name:string) => {
      toast.loading('PDF aanmaken...', { id:'generate-pdf' })
      const pdfDoc = await PDFDocument.create();
  
      for (const base64Image of base64Array) {
        const imageBytes = base64ToUint8Array(base64Image);
        let image;
  
        if (base64Image.startsWith('data:image/jpeg')) {
          image = await pdfDoc.embedJpg(imageBytes);
        } else if (base64Image.startsWith('data:image/png')) {
          image = await pdfDoc.embedPng(imageBytes);
        } else {
          toast.error('Unsupported image format', { id:'generate-pdf' });
          continue;
        }
  
        const page = pdfDoc.addPage();
        const { width, height } = image.scale(1);
        page.setSize(width, height);
        page.drawImage(image, {
          x: 0,
          y: 0,
          width: width,
          height: height,
        });
      }
  
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${name}.pdf`;
      document.body.appendChild(link);
      link.dispatchEvent(
        new MouseEvent('click', { 
          bubbles: true, 
          cancelable: true, 
          view: window 
        })
      );
      document.body.removeChild(link);

      toast.success('PDF aangemaakt', { id:'generate-pdf' })
    };

    function createNewBundle (base64:string) {
      let bundleName = `FitDoc Scan ${format(new Date(), "d MMM yyyy")}`
      setbundles(p => {
        let u = [...p]
        u.push({
          id: uuid(),
          name: bundleName,
          documents: [base64],
          date: new Date().toISOString()
        })
        return u
      })
    }

    function addToBundle (base64:string, id:string) {
      setbundles(p => {
        let u = [...p]
        const i = u.findIndex(i=>i.id===id);
        if(i>-1){
          u[i].documents.push(base64)
        }
        return u
      })
    }

    async function addImage (v:any, type:"file"|"camera"|"base", id:string|null){
      let createdBase = ''
      switch (type) {
        case "base":
          createdBase = v;
          break;
        default:
          break;
      }
      setopen_scanner(false);
      setselected_bundle(null);

      if(!id) { createNewBundle(createdBase) }
      else { addToBundle(createdBase, id) }
    }
  
    const [selected_bundle, setselected_bundle] = useState<string | null>(null);
    const [open_scanner, setopen_scanner] = useState(false);

    return (
      <div className='w-dvw h-[calc(100%-56px)] md:h-full flex flex-col'>
        <div className='w-full p-3 flex justify-center'>
          <p className='text-3xl font-bold'>Scanner</p>
        </div>

        <div className='h-full overflow-auto p-3 relative'>
          <BarcodeScanner 
            onSubmit={(b)=>{addImage(b, "base", selected_bundle);}}
            open={open_scanner} 
            onOpenChange={(o)=>{setopen_scanner(o)}} 
          />

          <div className='p-1 bg-primary rounded-full fixed right-3 bottom-3 flex items-center gap-1'>
            <button className='text-white w-9 h-9 rounded-full flex justify-center items-center hover:bg-white/10' onClick={()=>{setopen_scanner(true)}}><CameraIcon className='w-4 h-4'/></button>
          </div>

          {bundles.length === 0 &&
          <div className='flex items-center justify-center flex-col gap-2 py-16'>
            <div className='w-[120px]'>
              <img className='w-full h-auto' src={NoImages} />
            </div>
            <p className='max-w-sm text-center text-sm font-medium text-neutral-600'>U hebt geen scans</p>
            <p className='max-w-sm text-center text-sm text-neutral-600'>Begin een nieuwe scan vanuit uw camera of geïmporteerde foto's.</p>
          </div>
          }

          {bundles.length > 0 && 
            <div className='flex flex-col gap-3'>
              {bundles.map((bundle)=>(
              <div className='grid grid-cols-[100px,auto] gap-3 border bg-white pt-3 px-3 rounded-md overflow-hidden' key={bundle.id}>
                <BundleSheet 
                  bundle={bundle}
                  onEditBundle={(bundle)=>{setbundles(p=>{let u = [...p]; u[u.findIndex(i => i.id === bundle.id)] = bundle; return u;})}}
                  onClickAddScan={()=>{setselected_bundle(bundle.id); setopen_scanner(true)}}
                  onClickExport={()=>generatePDF(bundle.documents, bundle.name)}
                  onClickDelete={()=>setbundles(p=>p.filter(i=>i.id!==bundle.id))}
                />
                <div className='flex flex-col gap-4 pb-1'>
                  <div className='flex flex-col gap-2'>
                    <p className='text-sm text-neutral-600 font-medium'>{bundle.name}</p>
                    <p className='text-xs text-neutral-600'>{bundle.documents.length} documenten</p>
                    <p className='text-xs text-neutral-600'>{format(new Date(bundle.date), "d MMM yyyy")}</p>
                  </div>
                  <div className='flex items-center gap-1'>
                    <button onClick={()=>{setbundles(p=>p.filter(i=>i.id!==bundle.id))}} className='w-8 h-8 flex justify-center items-center text-neutral-600'><TrashIcon className='w-4 h-4'/></button>
                    <button onClick={()=>{generatePDF(bundle.documents, bundle.name)}} className='w-8 h-8 flex justify-center items-center text-neutral-600'><DownloadIcon className='w-4 h-4'/></button>
                    <button onClick={()=>{setselected_bundle(bundle.id); setopen_scanner(true); }} className='w-8 h-8 flex justify-center items-center text-neutral-600'><PlusIcon className='w-4 h-4'/></button>    
                  </div>
                </div>
              </div>     
              ))}
            </div>
          }
        </div>
      </div>
    );
  };

export default DocumentScanner;

function distanceFunc(p1:any, p2:any) {
    return Math.hypot(p1.x - p2.x, p1.y - p2.y);
}
  
function getCornerPoints(contour:any):any[] {
    let points:any[] = [];
    let rect = cv.minAreaRect(contour);
    const center = rect.center;
  
    let topLeftPoint;
    let topLeftDistance = 0;
  
    let topRightPoint;
    let topRightDistance = 0;
  
    let bottomLeftPoint;
    let bottomLeftDistance = 0;
  
    let bottomRightPoint;
    let bottomRightDistance = 0;
  
    for (let i = 0; i < contour.data32S.length; i += 2) {
      const point = { x: contour.data32S[i], y: contour.data32S[i + 1] };
      const distance = distanceFunc(point, center);
      if (point.x < center.x && point.y < center.y) {
        if (distance > topLeftDistance) {
          topLeftPoint = point;
          topLeftDistance = distance;
        }
      } else if (point.x > center.x && point.y < center.y) {
        if (distance > topRightDistance) {
          topRightPoint = point;
          topRightDistance = distance;
        }
      } else if (point.x < center.x && point.y > center.y) {
        if (distance > bottomLeftDistance) {
          bottomLeftPoint = point;
          bottomLeftDistance = distance;
        }
      } else if (point.x > center.x && point.y > center.y) {
        if (distance > bottomRightDistance) {
          bottomRightPoint = point;
          bottomRightDistance = distance;
        }
      }
    }
    points.push(topLeftPoint );
    points.push(topRightPoint as any);
    points.push(bottomRightPoint as any);
    points.push(bottomLeftPoint as any);
    return points;
}